import React, { useState, useEffect } from 'react';
import type { Settings } from '../types';

interface LiveThemeEditorProps {
    settings: Settings;
    onSave: (newSettings: Settings) => void;
}

const GearIcon: React.FC<{className?:string}> = ({className}) => <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg>;

const LiveThemeEditor: React.FC<LiveThemeEditorProps> = ({ settings, onSave }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [themeState, setThemeState] = useState(settings.theme.frontendTheme);

    useEffect(() => {
        setThemeState(settings.theme.frontendTheme);
    }, [settings.theme.frontendTheme]);

    useEffect(() => {
        if (!isOpen) return;

        const styleId = 'live-theme-preview-styles';
        let styleElement = document.getElementById(styleId);
        if (!styleElement) {
            styleElement = document.createElement('style');
            styleElement.id = styleId;
            document.head.appendChild(styleElement);
        }
        
        const feTheme = themeState;
        const fonts = feTheme.fontFamily;
        styleElement.innerHTML = `
            :root {
                --color-primary: ${feTheme.primaryColor};
                --color-secondary: ${feTheme.secondaryColor};
                --color-header-bg: ${feTheme.headerBackgroundColor};
                --color-footer-bg: ${feTheme.footerBackgroundColor};
                --color-nav-link: ${feTheme.navLinkColor};
                --color-nav-link-hover: ${feTheme.navLinkHoverColor};
                --font-family-header: '${fonts.header}';
                --font-family-heading: '${fonts.heading}';
                --font-family-body: '${fonts.body}';
            }
             body { background-color: ${feTheme.pageBackgroundColor}; }
        `;

        return () => {
            // On close, remove the preview styles so the saved settings apply
            styleElement?.remove();
        };

    }, [isOpen, themeState]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, dataset } = e.target;
        if (dataset.section === 'fontFamily') {
            setThemeState(prev => ({
                ...prev,
                fontFamily: { ...prev.fontFamily, [name]: value }
            }));
        } else {
            setThemeState(prev => ({ ...prev, [name]: value }));
        }
    };
    
    const handleSave = () => {
        const newSettings = {
            ...settings,
            theme: {
                ...settings.theme,
                frontendTheme: themeState
            }
        };
        onSave(newSettings);
        setIsOpen(false);
    };

    const handleReset = () => {
        setThemeState(settings.theme.frontendTheme);
    };
    
    const fontOptions = ['Inter', 'Playfair Display', 'Montserrat', 'Roboto Mono', 'Source Sans Pro', 'Lato', 'Raleway', 'Poppins', 'Nunito Sans', 'Open Sans'];

    return (
        <div className="fixed bottom-5 right-5 z-[999]">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-14 h-14 bg-gray-800 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-gray-700 transition-colors"
                title="Live Theme Editor"
            >
                <GearIcon className={`w-7 h-7 transition-transform ${isOpen ? 'animate-spin' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute bottom-20 right-0 w-80 bg-white rounded-lg shadow-2xl border border-gray-200 p-6 font-sans">
                    <h3 className="text-lg font-bold text-gray-800 mb-4">Live Theme Editor</h3>
                    <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
                        {/* Colors */}
                        <h4 className="font-semibold text-sm text-gray-600">Colors</h4>
                        {([
                            { name: 'primaryColor', label: 'Primary Text/Accent' },
                            { name: 'pageBackgroundColor', label: 'Page Background' },
                            { name: 'headerBackgroundColor', label: 'Header Background' },
                            { name: 'footerBackgroundColor', label: 'Footer Background' },
                            { name: 'navLinkColor', label: 'Nav Link (Default)' },
                            { name: 'navLinkHoverColor', label: 'Nav Link (Hover)' },
                            { name: 'activeLinkColor', label: 'Nav Link (Active)' },
                        ] as const).map(item => (
                            <div key={item.name}>
                                <label className="block text-xs font-medium text-gray-500">{item.label}</label>
                                <div className="flex items-center gap-2">
                                    <input type="color" name={item.name} value={themeState[item.name]} onChange={handleChange} className="h-8 w-10 rounded border-gray-300"/>
                                    <input type="text" name={item.name} value={themeState[item.name]} onChange={handleChange} className="w-full text-sm border-gray-300 rounded-md"/>
                                </div>
                            </div>
                        ))}
                        {/* Fonts */}
                        <h4 className="font-semibold text-sm text-gray-600 pt-2 border-t mt-4">Fonts</h4>
                        {([
                            { name: 'header', label: 'Header/Menu Font' },
                            { name: 'heading', label: 'Headings Font' },
                            { name: 'body', label: 'Body/Content Font' },
                        ] as const).map(item => (
                             <div key={item.name}>
                                <label className="block text-xs font-medium text-gray-500">{item.label}</label>
                                <select name={item.name} data-section="fontFamily" value={themeState.fontFamily[item.name]} onChange={handleChange} className="mt-1 block w-full text-sm border-gray-300 rounded-md">
                                    {fontOptions.map(font => <option key={font} value={font}>{font}</option>)}
                                </select>
                            </div>
                        ))}
                    </div>
                     <div className="flex justify-end space-x-2 mt-6 pt-4 border-t">
                        <button onClick={handleReset} className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">Reset</button>
                        <button onClick={handleSave} className="px-4 py-1.5 text-xs font-medium text-white bg-gray-800 rounded-md hover:bg-gray-700">Save</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LiveThemeEditor;